import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import config from '../config';
import { loadAppSettings } from '../utils/appSettings';
import { bbqTerm } from '../utils/bbqTerms';

const API = config.API_BASE_URL;

// Διαχείριση πόστων εταιρείας (8/8/2026): ο πελάτης ορίζει δικά του πόστα,
// και πολλαπλά ίδιου τύπου (π.χ. «Bar» + «Bar Πισίνας» σε ξενοδοχείο).
const slugify = (s) =>
  String(s).toLowerCase()
    .replace(/[άα]/g, 'a').replace(/[έε]/g, 'e').replace(/[ήη]/g, 'i').replace(/[ίιϊΐ]/g, 'i')
    .replace(/[όο]/g, 'o').replace(/[ύυϋΰ]/g, 'y').replace(/[ώω]/g, 'o').replace(/σς/g, 's')
    .replace(/[βγδζθκλμνξπρστφχψ]/g, (c) => ({ β:'v',γ:'g',δ:'d',ζ:'z',θ:'th',κ:'k',λ:'l',μ:'m',ν:'n',ξ:'x',π:'p',ρ:'r',σ:'s',τ:'t',φ:'f',χ:'ch',ψ:'ps' }[c] || ''))
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 32);

export default function StationsSettings() {
  const { t } = useTranslation();
  const [stations, setStations] = useState([]);
  const [form, setForm] = useState({ name: '', kind: 'generic', ready_notify: 'orderer' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/stations-config`).then((r) => setStations(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // Τι ψήνει το BBQ (κρέατα/ψάρια/μικτό) — προσαρμόζει εικονίδια/λεκτικά σε όλη την εφαρμογή
  const [bbqKindVal, setBbqKindVal] = useState('meat');
  useEffect(() => {
    axios.get(`${API}/company-settings`).then((r) => {
      if (r.data?.settings?.bbq_item_kind) setBbqKindVal(r.data.settings.bbq_item_kind);
    }).catch(() => {});
  }, []);
  const saveBbqKind = (v) => {
    setBbqKindVal(v);
    act(async () => {
      await axios.put(`${API}/company-settings`, { bbq_item_kind: v });
      await loadAppSettings(); // ανανέωση cache ώστε να αλλάξουν αμέσως τα λεκτικά
    }, t('Saved.'));
  };

  const act = async (fn, okText) => {
    setBusy(true); setMsg(null);
    try { await fn(); if (okText) setMsg({ ok: true, text: okText }); load(); }
    catch (e) {
      const code = e?.response?.data?.error;
      setMsg({ ok: false, text:
        code === 'key-exists' ? t('A station with this key already exists.')
        : code === 'in-use' ? t('This station is used by menu categories — move them first.')
        : t('Something went wrong — please try again.') });
    } finally { setBusy(false); }
  };

  const add = () => {
    const skey = slugify(form.name);
    if (!form.name.trim() || !skey) return;
    act(async () => {
      await axios.post(`${API}/stations-config`, { ...form, skey });
      setForm({ name: '', kind: 'generic', ready_notify: 'orderer' });
    }, t('Station added.'));
  };
  const save = (s, patch) => act(async () => { await axios.put(`${API}/stations-config/${s.id}`, patch); });
  const remove = (s) => {
    if (!window.confirm(t('Delete station "{{name}}"?', { name: s.name }))) return;
    act(async () => { await axios.delete(`${API}/stations-config/${s.id}`); }, t('Deleted.'));
  };
  const move = (idx, dir) => {
    const target = stations[idx + dir];
    if (!target) return;
    act(async () => {
      await axios.put(`${API}/stations-config/${stations[idx].id}`, { sort: idx + dir + 1 });
      await axios.put(`${API}/stations-config/${target.id}`, { sort: idx + 1 });
    });
  };

  const cell = { padding: '8px 10px', borderBottom: '1px solid #f3f4f6' };
  const input = { padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 10, fontSize: 14, width: 'auto' };

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, background: '#fff', maxWidth: 860 }}>
      <h3 style={{ marginTop: 0 }}>{t('Stations')}</h3>
      <p style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>
        {t('Define the stations of your business (kitchen, bars, pass...). Menu categories are routed to them, and each station can notify the waiter when a dish is ready.')}
      </p>
      {msg && (
        <div style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 10, fontSize: 13.5,
          background: msg.ok ? '#ecfdf5' : '#fef2f2', color: msg.ok ? '#047857' : '#b91c1c' }}>
          {msg.text}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, padding: '10px 12px', background: '#f9fafb', borderRadius: 10 }}>
        <label style={{ fontSize: 13.5, fontWeight: 600 }}>{t('What does your BBQ grill?')}</label>
        <select style={input} value={bbqKindVal} disabled={busy} onChange={(e) => saveBbqKind(e.target.value)}>
          <option value="meat">{t('Meats')} 🍖</option>
          <option value="fish">{t('Fish')} 🐟</option>
          <option value="mixed">{t('Mixed')} 🍢</option>
        </select>
        <span style={{ fontSize: 12, color: '#6b7280' }}>{t('Adjusts icons & wording across the app.')}</span>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr>
            {[t('Name'), t('Key'), t('Type'), t('When ready'), t('Active'), ''].map((h, i) => (
              <th key={i} style={{ ...cell, fontSize: 12, textTransform: 'uppercase', color: '#6b7280', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {stations.map((s, idx) => (
            <tr key={s.id}>
              <td style={cell}>
                <input style={input} type="text" autoCapitalize="off" defaultValue={s.name}
                  onBlur={(e) => e.target.value.trim() && e.target.value !== s.name && save(s, { name: e.target.value.trim() })} />
              </td>
              <td style={{ ...cell, color: '#6b7280', fontFamily: 'monospace', fontSize: 13 }}>{s.skey}</td>
              <td style={cell}>
                <select style={input} value={s.kind} disabled={busy} onChange={(e) => save(s, { kind: e.target.value })}>
                  <option value="generic">{t('Generic')}</option>
                  <option value="bbq">{bbqTerm('BBQ (meats board)')}</option>
                </select>
              </td>
              <td style={cell}>
                <select style={input} value={s.ready_notify} disabled={busy} onChange={(e) => save(s, { ready_notify: e.target.value })}>
                  <option value="orderer">{t('Notify the waiter')}</option>
                  <option value="none">{t('No notification')}</option>
                </select>
              </td>
              <td style={{ ...cell, textAlign: 'center' }}>
                <input type="checkbox" checked={!!s.active} disabled={busy}
                  onChange={(e) => save(s, { active: e.target.checked })} style={{ width: 18, height: 18 }} />
              </td>
              <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                <button disabled={busy || idx === 0} onClick={() => move(idx, -1)} style={{ border: 'none', background: 'none', cursor: 'pointer' }}>↑</button>
                <button disabled={busy || idx === stations.length - 1} onClick={() => move(idx, 1)} style={{ border: 'none', background: 'none', cursor: 'pointer' }}>↓</button>
                <button disabled={busy} onClick={() => remove(s)} title={t('Delete')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#b91c1c' }}>✕</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 14, flexWrap: 'wrap' }}>
        <input style={{ ...input, flex: '1 1 200px' }} type="text" autoCapitalize="off"
          placeholder={t('New station name (e.g. Pool Bar)')}
          value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <select style={input} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
          <option value="generic">{t('Generic')}</option>
          <option value="bbq">{t('BBQ (meats board)')}</option>
        </select>
        <button onClick={add} disabled={busy || !form.name.trim()}
          style={{ padding: '9px 18px', borderRadius: 10, border: 'none', background: '#111827', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
          + {t('Add station')}
        </button>
      </div>
      {form.name.trim() && (
        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>key: {slugify(form.name) || '—'}</div>
      )}
    </div>
  );
}
